/* eslint-disable @typescript-eslint/no-explicit-any */
import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { ApiChannel, CONFIG_TOKEN, StorageToken } from 'core/constants';
import { StoreAction } from 'store/actions';
import { buildHeaders } from '../http/options';
import { IXMOptions } from 'core/interfaces';

import { Fingerprint } from '../fingerprint';
import { Util } from '../util';
import { XmStore, XmStoreUtil } from '../store';
import { CimaToken } from './token';
import { CimaUrl } from './url';
import { Account, User } from 'store/user/models';
import { SessionStorage } from 'core/services';

const RESI_USER_TYPE: string = 'Residential';

@Injectable({
    providedIn: 'root'
})
export class CimaCore {
    private static PASSIVE_EVENT: string = 'OAUTH_EVENT';

    private config: IXMOptions;
    private http: HttpClient;
    private xmStore: XmStore;
    private cimaToken: CimaToken;
    private cimaUrl: CimaUrl;
    private refreshInterval: number;
    private fingerprint: Fingerprint;
    private sessionStorage: SessionStorage;

    constructor(
    @Inject(CONFIG_TOKEN) config: IXMOptions,
        http: HttpClient,
        xmStore: XmStore,
        cimaToken: CimaToken,
        cimaUrl: CimaUrl,
        fingerprint: Fingerprint,
        sessionStorage: SessionStorage
    ) {
        Object.assign(this, {
            config,
            http,
            xmStore,
            cimaToken,
            cimaUrl,
            fingerprint,
            sessionStorage
        });

        window.addEventListener('storage', this.storageListener.bind(this));
    }

    /**
     * Will check if the cima token was found in local storage and get one if it doesn't exist
     */
    public loadCimaToken(): Promise<string> {
        // we have a "code" from cima redirect which can then be used to get user access/refresh token
        if (this.cimaToken.code) {
            return this.getMobileAuthUserToken();
        } else if (this.cimaToken.accessToken) {
            this.setupRefreshToken();

            return Promise.resolve(this.cimaToken.accessToken);
        }

        return Promise.resolve(undefined);
    }

    /**
     * Public function that initiates the check to see if they are logged in with another comcast service
     */
    public tryPassiveAuthLogin(): void {
        if (!this.cimaToken.isLoggedIn) {
            this.passiveAuthIframe();
        }
    }

    /**
     * This is used to lazy load the account details and check if the user has an exist cart
     * NOTE: Only do this check if the user is logged in
     */
    public initUserData(): void {
        if (this.cimaToken.isLoggedIn) {
            XmStoreUtil.defaultCatch(this.xmStore.findChildPromise<Account, User>(StoreAction.GET_ACCOUNT_INFO, 'account', Account, User));
            this.fingerprint.initialize();
        }
    }

    /**
     * Check if the user is residential or buiness based on the CIMA ID token
     */

    public get isBusinessUser(): boolean {
        const idToken: string = this.cimaToken.idToken;

        return idToken && Util.decodeJWT(idToken) && Util.decodeJWT(idToken).user_type !== RESI_USER_TYPE;
    }

    /**
     * Get user access/refresh token from the "code" that we are given on redirect from cima
     */
    private getMobileAuthUserToken(fromPassiveAuth: boolean = false): Promise<string> {
        const params: any = {
            grant_type: 'authorization_code',
            code: this.cimaToken.code,
            redirect_uri: this.cimaUrl.apiRedirectUri(fromPassiveAuth),
            client_id: this.config.CLIENT_ID,
            ot: true
        };        
        
        if (this.sessionStorage.get(StorageToken.X_TENANT_ID)) {
            params.os = this.sessionStorage.get(StorageToken.CIMA_USER_ACCOUNT_GUID);
            params.tenantId = 'ORION';
        } else if (this.sessionStorage.get(StorageToken.CIMA_USER_ACCOUNT_GUID)) {
            params.acc = this.sessionStorage.get(StorageToken.CIMA_USER_ACCOUNT_GUID);
        }

        return this.http.post('/mobile-auth/token', undefined, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY }),
            params
        }).toPromise()
            .then((tokens: CimaResponse) => {
                this.cimaToken.saveNewToken(tokens.access_token, tokens.expires_in, tokens.refresh_token, tokens.id_token);
                this.setupRefreshToken();

                return this.cimaToken.accessToken;
            })
            .catch(() => Promise.resolve(undefined));
    }

    /**
     * Get new access/refresh token using the saved refresh token
     */
    private getMobileAuthUserTokenFromRefresh(): void {
        this.http.post('/mobile-auth/token', undefined, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY }),
            params: {
                grant_type: 'refresh_token',
                refresh_token: this.cimaToken.refreshToken,
                client_id: this.config.CLIENT_ID
            }
        }).toPromise()
            .then((tokens: CimaResponse) => {
                this.cimaToken.saveNewToken(tokens.access_token, tokens.expires_in, tokens.refresh_token, tokens.id_token);
                this.setupRefreshToken();

                return this.cimaToken.accessToken;
            })
            .catch(() => Promise.resolve(undefined));
    }

    private setupRefreshToken(): void {
        this.clearInterval();

        this.refreshInterval = window.setInterval(() => {
            // if the expiration time has passed, need to refresh the token
            if (this.cimaToken.isExpired()) {
                this.clearInterval();
                this.getMobileAuthUserTokenFromRefresh();
            }
        }, 10000);
    }

    private clearInterval(): void {
        if (this.refreshInterval) {
            window.clearInterval(this.refreshInterval);
            this.refreshInterval = undefined;
        }
    }

    private storageListener(event: StorageEvent): void {
        // only listen for our token
        if (event.key === 'xm-access-token') {
            if (!event.newValue) {
                this.cimaUrl.redirectToLogout();
            } else if (event.newValue !== event.oldValue) {
                // the case where another tab updated the token and we need to load the new token
                const foundNewToken: boolean = this.cimaToken.checkForNewTokens();
                if (foundNewToken) {
                    this.setupRefreshToken();
                }
            }
        }
    }

    /**
     * Build an iframe that checks if the user is logged in with another comcast service
     */
    private passiveAuthIframe(): void {
        // set listener for passive auth
        window.addEventListener(CimaCore.PASSIVE_EVENT, this.passiveAuthListener.bind(this), false);

        const queryParams: string = Util.createQueryParams({
            client_id: this.config.CLIENT_ID,
            redirect_uri: this.cimaUrl.apiRedirectUri(true),
            prompt: 'none',
            response_type: 'code'
        });

        const authIframe: HTMLIFrameElement = window.document.createElement('iframe');
        authIframe.setAttribute('src', `${this.config.CIMA_BASE_URL}/oauth/authorize${queryParams}${Util.hasCBMSSO() ? '&realm=commercial' : ''}`);
        authIframe.setAttribute('name', 'oauth-iframe');
        authIframe.setAttribute('id', 'oauth-iframe');
        authIframe.setAttribute('style', 'height: 0; width: 0; border: 0; position: absolute;');
        window.document.body.appendChild(authIframe);
    }

    /**
     * If the user is logged in with another comcast service, the code will get sent back here, which will allow
     * us to get the user cima token and then load any inital user data
     */
    private passiveAuthListener(event: CustomEvent): void {
        const code: string = event ? event.detail : '';
        if (code) {
            this.cimaToken.code = code;
            this.getMobileAuthUserToken(true).then(() => {
                this.initUserData();
            });
        }
    }
}
